/* eslint-disable max-lines */
import React, {
    useContext, useEffect, useMemo, useState
} from 'react';
import {
    useNavigate, useParams
} from 'react-router-dom';
import {
    assistantAuditService, opcodeAssistantService
} from 'shared/api';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    buildOrganizationMemberNames
} from 'shared/utils/organizationUtils';
import {
    Activity as ActivityIcon, AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Clock,
    GitBranch, Layers, MessageSquare, Radio, RefreshCw, Search, User
} from 'lucide-react';

const ACTIVITY_PAGE_SIZE = 200;

/**
 * ActivityPage — DE Studio → Activity.
 *
 * Action-level audit feed for assistant-adjacent work: assistant edits/runs,
 * opcode edits, channel binding changes, and instruction changes. Payloads are
 * intentionally small: action names and changed field names, not full diffs.
 */
export default function ActivityPage() {
    const {
        oragID, subOragID
    } = useParams();
    const navigate = useNavigate();
    const {
        selectedOrganization
    } = useContext(OrganizationContext);
    const [assistants, setAssistants] = useState([]);
    const [events, setEvents] = useState([]);
    const [assistantFilter, setAssistantFilter] = useState('');
    const [resourceFilter, setResourceFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: ACTIVITY_PAGE_SIZE,
        total: 0,
        total_pages: 1,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const memberNames = useMemo(
        () => buildOrganizationMemberNames(selectedOrganization),
        [selectedOrganization],
    );

    useEffect(() => {
        if (!oragID || !subOragID) return;
        setLoading(true);
        setError(null);
        opcodeAssistantService.listOpcodeAssistants(oragID, subOragID)
            .then((res) => setAssistants(Array.isArray(res?.data) ? res.data : []))
            .catch((err) => setError(err?.message || 'Failed to load'))
    }, [oragID, subOragID]);

    useEffect(() => {
        if (!oragID || !subOragID) return;
        setLoading(true);
        setError(null);
        assistantAuditService.listAssistantAuditEvents({
            org_id: oragID,
            sub_org_id: subOragID,
            assistant_id: assistantFilter || undefined,
            resource_type: resourceFilter || undefined,
            status: statusFilter || undefined,
            limit: ACTIVITY_PAGE_SIZE,
            page,
        })
            .then((res) => {
                const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
                const meta = res?.data?.pagination || {};
                setEvents(rows);
                setPagination({
                    page: Number(meta.page) || page,
                    limit: Number(meta.limit) || ACTIVITY_PAGE_SIZE,
                    total: Number(meta.total) || 0,
                    total_pages: Number(meta.total_pages) || 1,
                });
            })
            .catch((err) => setError(err?.response?.data?.error || err?.message || 'Failed to load activity'))
            .finally(() => setLoading(false));
    }, [oragID, subOragID, assistantFilter, resourceFilter, statusFilter, page]);

    const namesById = new Map(assistants.map((a) => [a.assistant_id, a.title || a.assistant_id]));
    const filteredEvents = events.filter((event) => {
        const needle = query.trim().toLowerCase();
        if (!needle) return true;
        return [
            event.action,
            event.assistant_id,
            namesById.get(event.assistant_id),
            event.resource_type,
            event.resource_id,
            actorLabel(event.actor_id, memberNames),
            event.status,
        ].some((value) => String(value || '').toLowerCase().includes(needle));
    });

    function openWorkspace(a) {
        const assistantId = typeof a === 'string' ? a : a?.assistant_id;
        if (!assistantId) return;
        navigate(`/organizations/${oragID}/sub/${subOragID}/de-studio/unified-builder/${assistantId}`);
    }

    function updateFilter(setter, value) {
        setter(value);
        setPage(1);
    }

    function goToPage(nextPage) {
        const clamped = Math.min(Math.max(nextPage, 1), pagination.total_pages || 1);
        setPage(clamped);
    }

    const pageStart = pagination.total === 0 ? 0 : ((pagination.page - 1) * pagination.limit) + 1;
    const pageEnd = Math.min(pagination.page * pagination.limit, pagination.total);

    return (
        <div className="ds-hub-page" style={{ padding: '20px 28px', height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                <div>
                    <h1 style={{ fontSize: 'var(--ds-text-2xl)', fontWeight: 700, margin: 0, color: 'var(--ds-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <ActivityIcon size={22} /> Activity
                    </h1>
                    <p style={{ margin: '4px 0 0', color: 'var(--ds-text-muted)', fontSize: 'var(--ds-text-sm)' }}>
                        Audit trail for assistant runs, edits, opcodes, channels, and instructions.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => {
                        setAssistantFilter('');
                        setResourceFilter('');
                        setStatusFilter('');
                        setQuery('');
                        setPage(1);
                    }}
                    style={buttonStyle}
                >
                    <RefreshCw size={15} /> Clear
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) 180px 150px minmax(220px, 1fr)', gap: 10, marginBottom: 14 }}>
                <label style={fieldStyle}>
                    <span style={labelStyle}>Assistant</span>
                    <select value={assistantFilter} onChange={(e) => updateFilter(setAssistantFilter, e.target.value)} style={inputStyle}>
                        <option value="">All assistants</option>
                        {assistants.map((a) => <option key={a.assistant_id} value={a.assistant_id}>{a.title || a.assistant_id}</option>)}
                    </select>
                </label>
                <label style={fieldStyle}>
                    <span style={labelStyle}>Resource</span>
                    <select value={resourceFilter} onChange={(e) => updateFilter(setResourceFilter, e.target.value)} style={inputStyle}>
                        <option value="">All</option>
                        <option value="assistant">Assistant</option>
                        <option value="opcode">Opcode</option>
                        <option value="channel">Channel</option>
                        <option value="instruction">Instruction</option>
                    </select>
                </label>
                <label style={fieldStyle}>
                    <span style={labelStyle}>Status</span>
                    <select value={statusFilter} onChange={(e) => updateFilter(setStatusFilter, e.target.value)} style={inputStyle}>
                        <option value="">All</option>
                        <option value="success">Success</option>
                        <option value="failure">Failure</option>
                    </select>
                </label>
                <label style={fieldStyle}>
                    <span style={labelStyle}>Search</span>
                    <span style={{ position: 'relative' }}>
                        <Search size={15} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--ds-text-subtle)' }} />
                        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Action, actor, resource..." style={{ ...inputStyle, paddingLeft: 32, width: '100%' }} />
                    </span>
                </label>
            </div>

            {error && (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: 'var(--ds-primary-soft)', color: 'var(--ds-primary-active)', fontSize: 'var(--ds-text-sm)', marginBottom: 12 }}>
                    {error}
                </div>
            )}

            <div style={{ border: '1px solid var(--ds-border)', borderRadius: 12, overflow: 'auto', background: 'var(--ds-surface)', minHeight: 0 }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 'var(--ds-text-sm)' }}>
                    <thead>
                        <tr>
                            <th style={thStyle('left')}>Time</th>
                            <th style={thStyle('left')}>Action</th>
                            <th style={thStyle('left')}>Assistant</th>
                            <th style={thStyle('left')}>Resource</th>
                            <th style={thStyle('left')}>Actor</th>
                            <th style={thStyle('left')}>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} style={tdCenter}>Loading activity...</td></tr>
                        ) : filteredEvents.length === 0 ? (
                            <tr><td colSpan={6} style={tdCenter}>No audit events found.</td></tr>
                        ) : filteredEvents.map((event) => (
                            <tr key={event._id || `${event.action}-${event.created_at}-${event.resource_id}`} className="ds-activity-row">
                                <td style={tdStyle('left')}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Clock size={14} />
                                        {formatStamp(event.created_at)}
                                    </div>
                                </td>
                                <td style={tdStyle('left')}>
                                    <span style={pillStyle(event.status)}>
                                        {event.status === 'failure' ? <AlertCircle size={13} /> : <CheckCircle2 size={13} />}
                                        {event.action}
                                    </span>
                                </td>
                                <td style={tdStyle('left')}>
                                    {event.assistant_id ? (
                                        <button type="button" onClick={() => openWorkspace(event.assistant_id)} style={linkButtonStyle}>
                                            {namesById.get(event.assistant_id) || event.assistant_id}
                                        </button>
                                    ) : <span style={{ color: 'var(--ds-text-subtle)' }}>Not attached</span>}
                                </td>
                                <td style={tdStyle('left')}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {resourceIcon(event.resource_type)}
                                        <span>{event.resource_type}</span>
                                    </div>
                                    <div style={monoStyle}>{event.resource_id}</div>
                                </td>
                                <td style={tdStyle('left')}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <User size={14} />
                                        <span title={event.actor_id || undefined}>
                                            {actorLabel(event.actor_id, memberNames)}
                                        </span>
                                    </div>
                                </td>
                                <td style={tdStyle('left')}>
                                    {detailsText(event.metadata)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={paginationStyle}>
                <span>
                    Showing {pageStart}-{pageEnd} of {pagination.total}
                    {query.trim() ? ` · ${filteredEvents.length} matching this page` : ''}
                </span>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <button
                        type="button"
                        onClick={() => goToPage(pagination.page - 1)}
                        disabled={loading || pagination.page <= 1}
                        style={pagerButtonStyle(loading || pagination.page <= 1)}
                        title="Previous page"
                    >
                        <ChevronLeft size={15} />
                    </button>
                    <span>Page {pagination.page} of {pagination.total_pages}</span>
                    <button
                        type="button"
                        onClick={() => goToPage(pagination.page + 1)}
                        disabled={loading || pagination.page >= pagination.total_pages}
                        style={pagerButtonStyle(loading || pagination.page >= pagination.total_pages)}
                        title="Next page"
                    >
                        <ChevronRight size={15} />
                    </button>
                </div>
            </div>
        </div>
    );
}

const PRINCIPAL_SOURCES = [
    { prefix: 'agent:', suffix: ' · agent', unknown: (id) => `${id} · agent` },
    { prefix: 'mcp:', suffix: ' · MCP', unknown: () => 'MCP client' },
];

function actorLabel(actorId, memberNames) {
    if (!actorId) return 'system';
    const principal = String(actorId);

    for (const { prefix, suffix, unknown } of PRINCIPAL_SOURCES) {
        if (!principal.startsWith(prefix)) continue;
        const id = principal.slice(prefix.length);
        const name = memberNames.get(id);
        return name ? `${name}${suffix}` : unknown(id);
    }

    return memberNames.get(principal) || principal;
}

function formatStamp(value) {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString();
}

function detailsText(metadata = {}) {
    const fields = Array.isArray(metadata.changed_fields) ? metadata.changed_fields : [];
    const bits = [];
    if (fields.length) bits.push(`Changed: ${fields.join(', ')}`);
    if (metadata.opcode_id) bits.push(`Opcode: ${metadata.opcode_id}`);
    if (Array.isArray(metadata.opcode_ids) && metadata.opcode_ids.length) bits.push(`Opcodes: ${metadata.opcode_ids.join(', ')}`);
    if (metadata.channel_id) bits.push(`Channel: ${metadata.channel_id}`);
    if (metadata.response_status) bits.push(`HTTP ${metadata.response_status}`);
    if (metadata.error) bits.push(metadata.error);
    return bits.length ? bits.join(' · ') : '-';
}

function resourceIcon(type) {
    if (type === 'opcode') return <GitBranch size={14} />;
    if (type === 'channel') return <Radio size={14} />;
    if (type === 'instruction') return <MessageSquare size={14} />;
    if (type === 'assistant') return <Layers size={14} />;
    return <ActivityIcon size={14} />;
}

const fieldStyle = { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 };
const labelStyle = { fontSize: 'var(--ds-text-xs)', color: 'var(--ds-text-subtle)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' };
const inputStyle = {
    padding: '8px 10px', borderRadius: 8, fontSize: 'var(--ds-text-sm)',
    border: '1px solid var(--ds-border-strong)', background: 'var(--ds-surface)',
    color: 'var(--ds-text)', minHeight: 38,
};
const buttonStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 14px',
    borderRadius: 8, fontSize: 'var(--ds-text-sm)', fontWeight: 600,
    border: '1px solid var(--ds-border-strong)', background: 'var(--ds-surface)',
    color: 'var(--ds-text)', cursor: 'pointer', height: 38,
};
const linkButtonStyle = {
    border: 0, padding: 0, background: 'transparent', color: 'var(--ds-primary)',
    font: 'inherit', fontWeight: 600, cursor: 'pointer', textAlign: 'left',
};
const paginationStyle = {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    paddingTop: 12,
    color: 'var(--ds-text-muted)',
    fontSize: 'var(--ds-text-xs)',
};
function pagerButtonStyle(disabled) {
    return {
        width: 30,
        height: 30,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        border: '1px solid var(--ds-border-strong)',
        background: 'var(--ds-surface)',
        color: disabled ? 'var(--ds-text-subtle)' : 'var(--ds-text)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
    };
}
const monoStyle = { fontFamily: 'monospace', fontSize: 'var(--ds-text-2xs)', color: 'var(--ds-text-subtle)' };
function pillStyle(status) {
    return {
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px',
        borderRadius: 999, fontSize: 'var(--ds-text-xs)', fontWeight: 700,
        color: status === 'failure' ? '#B42318' : '#087443',
        background: status === 'failure' ? '#FEE4E2' : '#DCFAE6',
        whiteSpace: 'nowrap',
    };
}
function thStyle(align) {
    return {
        textAlign: align, padding: '10px 14px', fontSize: 'var(--ds-text-xs)',
        letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ds-text-subtle)',
        fontWeight: 700, background: 'var(--ds-surface-2)', borderBottom: '1px solid var(--ds-border)',
        whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1,
    };
}
function tdStyle(align) {
    return {
        textAlign: align, padding: '11px 14px', borderTop: '1px solid var(--ds-divider)',
        color: 'var(--ds-text-2)', verticalAlign: 'middle',
    };
}
const tdCenter = { textAlign: 'center', padding: '40px 14px', color: 'var(--ds-text-muted)' };
